import React from 'react';
import { Close } from '@mui/icons-material';

// Pill geometry shared by every badge beside a ticker — labels, and the strategy
// badge (issue #411). Layout lives inline because `inline-flex`, `gap-1` and
// `items-start` are absent from the prebuilt tailwind-output.css. `alignSelf`
// is what keeps a pill at its own height: without it the name cell's flex row
// (whose `items-start` never applied) stretched a pill sitting beside a short
// name to the name's line height, while one wrapped under a long name stayed compact.
export const pillClass = (size = 'sm') => `rounded-full ${size === 'md' ? 'text-sm px-2.5 py-1' : 'text-xs px-2 py-0.5'}`;
export const PILL_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'center', whiteSpace: 'nowrap' };

/**
 * LabelBadge - Colored pill showing a label name
 *
 * @param {Object} props
 * @param {Object} props.label - Label object with id, name, color, trackPurchases
 * @param {boolean} props.removable - Show remove button
 * @param {function} props.onRemove - Called when remove button clicked
 * @param {function} props.onClick - Called when badge is clicked
 * @param {string} props.size - 'sm' | 'md' (default: 'sm')
 * @param {string} props.className - Additional CSS classes
 */
const LabelBadge = ({
  label,
  removable = false,
  onRemove,
  onClick,
  size = 'sm',
  className = ''
}) => {
  if (!label) return null;

  // Calculate text color based on background brightness
  const getTextColor = (bgColor) => {
    // Remove # if present
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  };

  const textColor = getTextColor(label.color);

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onRemove?.(label);
  };

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      e.preventDefault();
      onClick(label);
    }
  };

  return (
    <span
      className={`
        ${pillClass(size)}
        ${onClick ? 'cursor-pointer hover:opacity-80' : ''}
        ${className}
      `}
      style={{
        ...PILL_STYLE,
        backgroundColor: label.color,
        color: textColor
      }}
      onClick={handleClick}
      title={label.trackPurchases ? `${label.name} (tracking purchases)` : label.name}
    >
      <span className="truncate max-w-[100px]">{label.name}</span>
      {removable && (
        <button
          type="button"
          onClick={handleRemoveClick}
          className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
          title={`Remove ${label.name}`}
        >
          <Close style={{ fontSize: size === 'sm' ? 12 : 14, color: textColor }} />
        </button>
      )}
    </span>
  );
};

export default LabelBadge;
